import React, { useMemo } from "react";
import { Box } from "@mui/material";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { useGetUsers } from "../../../shared/hooks";

interface QueryFiltersProps {
    filter: any;
    handleUpdate: (key: string, value: any) => void;
    isPendingPhase: boolean;
    phaseOptions: { id: string; name: string }[];
    isProjectsearchDisabled: boolean;
    hiddenFilters?: string[];
};

export const QueryFilters: React.FC<QueryFiltersProps> = ({ filter, handleUpdate, isPendingPhase, phaseOptions, isProjectsearchDisabled, hiddenFilters }) => {

    const { getFilterOptions } = useDisplayField([]);

    const { data: usersData, isLoading: isLoadingUsers } = useGetUsers();
    const userOptions = useMemo(() => {
        if (!usersData?.data) return [];
        return usersData.data
            .filter((u) => u.is_active === "1")
            .map((u) => ({
                value: u.user_id,
                label: `${u.first_name} ${u.last_name}`,
            }));
    }, [usersData]);

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} p={2}>

            <FieldControl
                keyName={'priority'}
                value={filter.priority}
                label={"Query Priority"}
                variant={"select"}
                options={getFilterOptions('QUERY_PRIORITY').map((value) => {
                    return {
                        label: value.display_text,
                        value: value.key
                    }
                })}
                onChange={handleUpdate}
                multiSelect
            />

            <FieldControl
                keyName={'status'}
                value={filter.status}
                label={"Query Status"}
                variant={"select"}
                options={getFilterOptions('QUERY_STATUS').map((value) => {
                    return {
                        label: value.display_text,
                        value: value.key
                    }
                })}
                onChange={handleUpdate}
                multiSelect
            />

            <FieldControl
                keyName={'category'}
                value={filter.category}
                label={"Query Category"}
                variant={"select"}
                options={getFilterOptions('QUERY_CATEGORY').map((value) => {
                    return {
                        label: value.display_text,
                        value: value.key
                    }
                })}
                onChange={handleUpdate}
                multiSelect
            />

            <ControlledAutocomplete
                type="PROJECT"
                value={filter.project}
                onSelect={(values) => handleUpdate("project", values)}
                label="Search project"
                placeholder="Search project"
                disabled={isProjectsearchDisabled}
            />

            {
                hiddenFilters?.includes('portfolio') &&
                <ControlledAutocomplete
                    type="PORTFOLIO"
                    multiSelect
                    value={filter.portfolio}
                    onSelect={(values) => handleUpdate("portfolio", values)}
                    label="Search portfolio"
                    placeholder="Search portfolio"
                />
            }

            {
                hiddenFilters?.includes('programme') &&
                <ControlledAutocomplete
                    type="PROGRAMME"
                    multiSelect
                    value={filter.programme}
                    onSelect={(values) => handleUpdate("programme", values)}
                    label="Search programme"
                    placeholder="Search programme"
                />
            }

            <FieldControl
                keyName={'phase'}
                value={filter.phase}
                label={"Select Project Phase"}
                variant={"select"}
                options={
                    phaseOptions.map((value: { id: string, name: string; }) => {
                        return {
                            value: value.id, label: value.name
                        }
                    })
                }
                onChange={handleUpdate}
                required
                loading={isPendingPhase}
                multiSelect
            />

            <FieldControl
                keyName={'assigned'}
                value={filter.assigned}
                label={"Assigned to"}
                variant={"select"}
                options={userOptions}
                loading={isLoadingUsers}
                onChange={handleUpdate}
                required
                multiSelect
            />

        </Box>
    )
}